import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CustomerProductPricingResponse,
  CustomerSearchResponse,
  ProductCustomerPricingResponse,
  ProductSearchResponse,
  SalespersonSearchResponse,
} from './types'

const ROOT = '/api/customer-product-pricing'

export type PricingSort = 'margin_pct' | 'last_order_date' | 'price_per_kg'

/** Filters shared by both lookup directions — date range, margin-floor
 *  threshold, and the salesperson/vendor substring filters the True
 *  Margin engine already supports. Empty strings are omitted from the
 *  query string rather than sent literally (an empty `salesperson=`
 *  would be indistinguishable from "filter to blank salesperson"). */
export interface PricingFilters {
  dateFrom: string
  dateTo: string
  marginFloorPct: number
  salesperson: string
  vendorName: string
}

function filterParams(filters: PricingFilters): string {
  const params = new URLSearchParams()
  if (filters.dateFrom) params.set('date_from', filters.dateFrom)
  if (filters.dateTo) params.set('date_to', filters.dateTo)
  params.set('margin_floor_pct', String(filters.marginFloorPct))
  if (filters.salesperson.trim()) params.set('salesperson', filters.salesperson.trim())
  if (filters.vendorName.trim()) params.set('vendor_name', filters.vendorName.trim())
  return params.toString()
}

export function useCustomerProductPricing(
  productId: number | null,
  sort: PricingSort,
  filters: PricingFilters,
) {
  return useQuery({
    queryKey: ['customer-product-pricing', productId, sort, filters],
    queryFn: () =>
      jsonFetch<CustomerProductPricingResponse>(
        `${ROOT}/${productId}?sort=${sort}&${filterParams(filters)}`,
      ),
    enabled: productId !== null,
    staleTime: 15_000,
  })
}

export function useProductPricingForCustomer(
  customerId: number | null,
  sort: PricingSort,
  filters: PricingFilters,
) {
  return useQuery({
    queryKey: ['customer-product-pricing', 'by-customer', customerId, sort, filters],
    queryFn: () =>
      jsonFetch<ProductCustomerPricingResponse>(
        `${ROOT}/by-customer/${customerId}?sort=${sort}&${filterParams(filters)}`,
      ),
    enabled: customerId !== null,
    staleTime: 15_000,
  })
}

// Odoo.sh briefly 429s (and occasionally returns an HTML throttle page
// instead of JSON) under bursts of rapid search-as-you-type calls — seen
// live against /api/pricelists/search/partners while typing quickly.
// The app's global QueryClient default is `retry: 1`, too little to ride
// out a short-lived throttle window; these two search hooks override it
// so a transient 429/HTML blip resolves silently on retry instead of
// surfacing a hard "Couldn't load" error to the user.
const SEARCH_RETRY = 3

export function useProductSearchForPricing(query: string, enabled = true) {
  return useQuery({
    queryKey: ['product-customer-pricing', 'product-search', query],
    queryFn: () => {
      const q = query.trim() ? `q=${encodeURIComponent(query.trim())}&` : ''
      // Samples (SAMP/SMPL default_code prefix, or the "Samples" product
      // category) are never real pricing decisions — exclude them here.
      return jsonFetch<ProductSearchResponse>(`/api/pricelists/search/products?${q}exclude_samples=1`)
    },
    enabled,
    staleTime: 30_000,
    retry: SEARCH_RETRY,
  })
}

/** Reverse-direction search — same `/api/pricelists/search/partners`
 *  endpoint `PartnerPicker` uses (`kind=customer`), reused here as a
 *  plain list rather than a popover picker.
 *
 *  `salespersonId`, when set, narrows to that rep's assigned book (via
 *  the backend's `res.partner.user_id`/`user_id_2` lookup) and raises
 *  the result cap to 200 — a rep's whole customer list, not just the
 *  first page — since the point of picking a salesperson is "show me
 *  everyone assigned to them," not a name-filtered subset. Blank `q` is
 *  intentional in that case: the query key still includes it so the
 *  narrowed list is cached separately from the unfiltered one. */
export function useCustomerSearchForPricing(
  query: string,
  enabled = true,
  salespersonId?: number,
) {
  return useQuery({
    queryKey: ['product-customer-pricing', 'customer-search', query, salespersonId],
    queryFn: () => {
      const params = new URLSearchParams({ q: query.trim(), kind: 'customer' })
      if (salespersonId != null) {
        params.set('salesperson_id', String(salespersonId))
        params.set('limit', '200')
      } else {
        params.set('limit', '20')
      }
      return jsonFetch<CustomerSearchResponse>(`/api/pricelists/search/partners?${params.toString()}`)
    },
    enabled,
    staleTime: 30_000,
    retry: SEARCH_RETRY,
  })
}

/** Backs the salesperson filter on the customer picker — only reps who
 *  currently own at least one customer are returned (see the backend
 *  docstring), so a selection here never dead-ends into an empty book. */
export function useSalespeopleSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['product-customer-pricing', 'salesperson-search', query],
    queryFn: () =>
      jsonFetch<SalespersonSearchResponse>(
        `/api/pricelists/search/salespeople?q=${encodeURIComponent(query.trim())}`,
      ),
    enabled,
    staleTime: 30_000,
    retry: SEARCH_RETRY,
  })
}
